"use client";

import { usePathname } from "next/navigation";
import { isPublicUrl } from "src/config/publicUrls";
import { SidePanelOutlet } from "src/components/ui/side-panel/SidePanelOutlet";
import { SidePanelChrome, SidePanelProvider } from "src/components/ui/side-panel/SidePanelProvider";
import { Sidebar } from "./projects/Sidebar";

/**
 * Chooses the app chrome — sidebar or bare full width — from the LIVE pathname.
 *
 * This cannot be decided in the root layout. That layout renders once per
 * document load and Next.js reuses it across every client-side navigation, so
 * anything it computes from the request path is frozen for the life of the
 * document: a session that starts on `/logout` and soft-navigates back into the
 * app would keep the bare public chrome, with no sidebar and no content offset,
 * until a full reload. `usePathname()` re-renders on navigation, so the chrome
 * tracks the route the way the profile query does.
 *
 * The app chrome also hosts side panels: the provider (live region, F6) and the
 * outer `SidePanelOutlet`, used by pages outside a project. Project pages get
 * their own outlet under the navbar, which wins because it is deeper. The
 * sidebar is chrome that an open task panel makes inert.
 */
export function AppShell({
  children,
  smallLogo,
  largeLogo,
}: Readonly<{
  children: React.ReactNode;
  smallLogo: React.ReactNode;
  largeLogo: React.ReactNode;
}>) {
  const pathname = usePathname();

  if (isPublicUrl(pathname ?? "")) {
    return <div className="h-screen">{children}</div>;
  }

  return (
    <SidePanelProvider>
      <div>
        <SidePanelChrome>
          <Sidebar smallLogo={smallLogo} largeLogo={largeLogo} />
        </SidePanelChrome>
        {/* The outlet itself is viewport tall, so its panel column and panels have a definite
            height to fill: their bodies scroll and their footers stay in view. */}
        <SidePanelOutlet className="h-screen" pageClassName="ml-16 h-full min-h-0">
          {children}
        </SidePanelOutlet>
      </div>
    </SidePanelProvider>
  );
}
